import React, { useCallback, useMemo, useEffect } from 'react';
import { Snackbar, Typography, Link, IconButton, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage } from 'react-intl';
import { Alert } from '@mui/material';
import classNames from 'classnames';
import { useMessages } from 'modules/intl';
import { SlideTransition } from 'modules/common/components';
import { useSnackbarStyles } from 'modules/common/hooks/useSnackbarStyles';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';

type TimesheetSnackBarProps = {
  showSnackbar: boolean;
  successCount: number;
  totalCount: number;
  setOpenErrorDialog: (open: boolean) => void;
};

type PageStyleProps = {
  pinned: boolean;
};

const useStyles = makeStyles((theme) => ({
  snackBar: {
    left: ({ pinned }: PageStyleProps) => (pinned ? 240 : 64),
    flexDirection: 'column',
    alignItems: 'flex-start',
    bottom: 20
  },
  item: {
    marginBottom: '5px',
    minWidth: '300px',
    fontWeight: 'bold'
  },
  close: {
    padding: theme.spacing(0.5)
  }
}));

export const TimesheetSnackBar: React.FC<TimesheetSnackBarProps> = ({
  showSnackbar,
  successCount,
  totalCount,
  setOpenErrorDialog
}: TimesheetSnackBarProps) => {
  const { pinned = false } = useSidePanelContext();
  const classes = useStyles({ pinned });
  const [open, setOpen] = React.useState(false);
  const [durationOpen, setDurationOpen] = React.useState(3000);
  const snackbarClasses = useSnackbarStyles({});
  const snackbarErrorClasses = useSnackbarStyles({ category: 'error' });

  const errorCount = useMemo(() => totalCount - successCount, [
    totalCount,
    successCount
  ]);

  const showError = errorCount > 0;
  const showSuccess = successCount > 0;

  const messages = useMessages(
    [
      'smartDiary.sychMessages.allSuccess',
      'smartDiary.sychMessages.partialSuccess',
      'smartDiary.sychMessages.failureMessage'
    ],
    {
      successCount: successCount,
      errorCount: errorCount,
      totalCount: totalCount
    }
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const openErrorDialog = useCallback(() => {
    setOpenErrorDialog(true);
  }, [setOpenErrorDialog]);

  const iconButton = useMemo(
    () => (
      <IconButton
        aria-label="close"
        color="inherit"
        data-testid="close-snackbar"
        className={classes.close}
        onClick={handleClose}
        size="large"
      >
        <CloseIcon />
      </IconButton>
    ),
    [classes.close, handleClose]
  );

  useEffect(() => {
    if (showError) setDurationOpen(6000);
    if (showSnackbar && (showSuccess || showError)) setOpen(true);
  }, [showSnackbar, showSuccess, showError]);

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      open={open}
      autoHideDuration={durationOpen}
      onClose={handleClose}
      className={classes.snackBar}
      TransitionComponent={SlideTransition}
    >
      <div>
        {showSuccess ? (
          <Alert
            severity="success"
            variant="filled"
            data-testid="message-success"
            className={classNames(classes.item, snackbarClasses.severity)}
          >
            <Typography variant="body1">
              {successCount === totalCount
                ? messages['smartDiary.sychMessages.allSuccess']
                : messages['smartDiary.sychMessages.partialSuccess']}
            </Typography>
          </Alert>
        ) : null}
        {showError ? (
          <Alert
            severity="error"
            variant="filled"
            data-testid="message-failure"
            className={classNames(classes.item, snackbarErrorClasses.severity)}
            action={iconButton}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Typography variant="body1">
                  {messages['smartDiary.sychMessages.failureMessage']}
                </Typography>
              </Grid>
              <Grid item>
                <Link href="#" onClick={openErrorDialog}>
                  <Typography variant="body1">
                    <FormattedMessage id="smartDiary.sychMessages.linkTitle" />
                  </Typography>
                </Link>
              </Grid>
            </Grid>
          </Alert>
        ) : null}
      </div>
    </Snackbar>
  );
};
